import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadialBarChart, RadialBar
} from 'recharts';
import { 
  BarChart3, PieChart as PieChartIcon, TrendingUp, TrendingDown,
  CheckCircle2, XCircle, Clock, Target, Sparkles, ArrowRight,
  RefreshCw, Filter, Calendar, AlertTriangle, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#06b6d4', '#8b5cf6', '#f97316', '#22c55e', '#ef4444', '#eab308'];

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'primary' }) => {
  const colorClasses = {
    primary: 'from-cyan-500/20 to-cyan-600/5 text-cyan-500',
    success: 'from-emerald-500/20 to-emerald-600/5 text-emerald-500',
    warning: 'from-yellow-500/20 to-yellow-600/5 text-yellow-500',
    danger: 'from-red-500/20 to-red-600/5 text-red-500',
    purple: 'from-purple-500/20 to-purple-600/5 text-purple-500'
  };

  return (
    <Card className="overflow-hidden" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        {trend && (
          <div className="mt-4 flex items-center gap-2 text-sm">
            {trend.direction === 'up' ? (
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
            <span className={trend.direction === 'up' ? 'text-emerald-500' : 'text-red-500'}>
              {trend.value}
            </span>
            <span className="text-muted-foreground">{trend.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AutoPIPAnalytics() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auto-pip/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  const fetchHistory = async (status = null) => {
    try {
      const url = status && status !== 'all' 
        ? `${API_URL}/api/auto-pip/history?status=${status}`
        : `${API_URL}/api/auto-pip/history`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.auto_pips || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchAnalytics(), fetchHistory(historyFilter)]);
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAnalytics(), fetchHistory()]);
      setLoading(false);
    };
    loadData();
  }, [token]);

  useEffect(() => {
    fetchHistory(historyFilter);
  }, [historyFilter]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="analytics-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const summary = analytics?.summary || {};

  return (
    <div className="space-y-6" data-testid="auto-pip-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-primary" />
            Auto-PIP Analytics
          </h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive analytics and outcomes tracking for automated PIPs
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/auto-pip">
            <Button variant="outline" className="gap-2">
              <Sparkles className="h-4 w-4" />
              Generate PIPs
            </Button>
          </Link>
          <Button onClick={handleRefresh} disabled={refreshing} variant="outline" className="gap-2">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Generated"
          value={summary.total_generated || 0}
          subtitle="All time Auto-PIPs"
          icon={Sparkles}
          color="primary"
        />
        <StatCard
          title="Pending Review"
          value={summary.pending || 0}
          subtitle="Awaiting decision"
          icon={Clock}
          color="warning"
        />
        <StatCard
          title="Approved"
          value={summary.approved || 0}
          subtitle="Converted to active PIPs"
          icon={CheckCircle2}
          color="success"
        />
        <StatCard
          title="Conversion Rate"
          value={`${summary.conversion_rate || 0}%`}
          subtitle="Approved / Generated"
          icon={Target}
          color="purple"
        />
        <StatCard
          title="Avg. Approval Time"
          value={`${summary.avg_approval_days || 0}d`}
          subtitle="Days to approval"
          icon={Calendar}
          color="primary"
        />
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="metrics">Triggered Metrics</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Status Distribution
                </CardTitle>
                <CardDescription>Breakdown of Auto-PIP statuses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics?.status_distribution || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {(analytics?.status_distribution || []).map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  {(analytics?.status_distribution || []).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm">{item.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Priority Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Priority Distribution
                </CardTitle>
                <CardDescription>Auto-PIPs by AI-assigned priority</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics?.priority_distribution || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={80} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {(analytics?.priority_distribution || []).map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Category Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Category Distribution
                </CardTitle>
                <CardDescription>Auto-PIPs by metric category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics?.category_distribution || []}
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {(analytics?.category_distribution || []).map((entry, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Trigger Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5" />
                  Trigger Analysis
                </CardTitle>
                <CardDescription>Why Auto-PIPs were generated</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics?.trigger_analysis || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Weekly Generation Trends
              </CardTitle>
              <CardDescription>Auto-PIP generation and approval trends over the past 4 weeks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics?.weekly_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="generated" 
                      stroke="#06b6d4" 
                      strokeWidth={2}
                      name="Generated"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="approved" 
                      stroke="#22c55e" 
                      strokeWidth={2}
                      name="Approved"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="rejected" 
                      stroke="#ef4444" 
                      strokeWidth={2}
                      name="Rejected"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Conversion Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Conversion Funnel</CardTitle>
              <CardDescription>Auto-PIP journey from generation to completion</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 text-center p-6 bg-cyan-500/10 rounded-lg">
                  <p className="text-4xl font-bold text-cyan-500">{summary.total_generated || 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Generated</p>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex-1 text-center p-6 bg-yellow-500/10 rounded-lg">
                  <p className="text-4xl font-bold text-yellow-500">{summary.pending || 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Pending</p>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex-1 text-center p-6 bg-emerald-500/10 rounded-lg">
                  <p className="text-4xl font-bold text-emerald-500">{summary.approved || 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Approved</p>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex-1 text-center p-6 bg-purple-500/10 rounded-lg">
                  <p className="text-4xl font-bold text-purple-500">{analytics?.outcome_stats?.completed || 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Completed</p>
                </div>
              </div>
              <div className="mt-6">
                <div className="flex justify-between text-sm mb-2">
                  <span>Conversion Rate</span>
                  <span className="font-medium">{summary.conversion_rate || 0}%</span>
                </div>
                <Progress value={summary.conversion_rate || 0} className="h-2" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Metrics Tab */}
        <TabsContent value="metrics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Most Triggered Metrics
              </CardTitle>
              <CardDescription>Metrics that most frequently trigger Auto-PIP generation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(analytics?.top_triggered_metrics || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-muted-foreground">#{idx + 1}</span>
                      <div>
                        <p className="font-medium">{item.metric}</p>
                        <p className="text-sm text-muted-foreground">{item.count} Auto-PIPs generated</p>
                      </div>
                    </div>
                    <div className="w-32">
                      <Progress 
                        value={(item.count / Math.max(...(analytics?.top_triggered_metrics || []).map(m => m.count), 1)) * 100} 
                        className="h-2" 
                      />
                    </div>
                  </div>
                ))}
                {(analytics?.top_triggered_metrics || []).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No triggered metrics yet</p>
                    <p className="text-sm">Generate Auto-PIPs to see which metrics need attention</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Auto-PIP History
                  </CardTitle>
                  <CardDescription>Complete history of generated Auto-PIPs</CardDescription>
                </div>
                <Select value={historyFilter} onValueChange={setHistoryFilter}>
                  <SelectTrigger className="w-40">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history.map((pip, idx) => (
                  <div 
                    key={pip.id || idx} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-2 w-2 rounded-full ${
                        pip.status === 'approved' ? 'bg-emerald-500' :
                        pip.status === 'rejected' ? 'bg-red-500' : 'bg-yellow-500'
                      }`} />
                      <div>
                        <p className="font-medium">{pip.metric_display_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {pip.category?.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(pip.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={
                        pip.ai_priority === 'critical' ? 'bg-red-500' :
                        pip.ai_priority === 'high' ? 'bg-orange-500' :
                        pip.ai_priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                      }>
                        {pip.ai_priority}
                      </Badge>
                      <Badge variant={
                        pip.status === 'approved' ? 'default' :
                        pip.status === 'rejected' ? 'destructive' : 'secondary'
                      }>
                        {pip.status === 'approved' ? (
                          <><CheckCircle2 className="h-3 w-3 mr-1" /> Approved</>
                        ) : pip.status === 'rejected' ? (
                          <><XCircle className="h-3 w-3 mr-1" /> Rejected</>
                        ) : (
                          <><Clock className="h-3 w-3 mr-1" /> Pending</>
                        )}
                      </Badge>
                    </div>
                  </div>
                ))}
                {history.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No Auto-PIPs found</p>
                    <p className="text-sm mt-1">Generate Auto-PIPs from the analysis page</p>
                    <Link to="/auto-pip">
                      <Button variant="outline" className="mt-4">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Go to Auto-PIP Generator
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
